import axios from 'axios';
import tokenManager from '../utils/tokenManager';
import performanceService from './performanceService';
import { supabase } from '../config/supabase';
import logger from '../utils/logger';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.releasecadence.dev/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,  // Important for CORS with credentials
  timeout: 10000,        // 10 second timeout
});

// Add request interceptor to add auth token and track performance
api.interceptors.request.use(
  async (config) => {
    // Start performance tracking
    const startTime = performance.now();
    config._startTime = startTime;
    
    // Skip token check for auth-related and public endpoints
    const skipAuth = 
      config.url.includes('/auth/login') ||
      config.url.includes('/auth/register') ||
      config.url.includes('/auth/password-reset') ||
      config.url.includes('/public') ||
      config.url.includes('/survey/') ||
      config.url.includes('/attachments/public');

    if (!skipAuth) {
      // Get token from cache (tokenManager handles localStorage fallback)
      const token = tokenManager.getToken();
      
      logger.debug(`[API] Token check for ${config.url}:`, {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
      });

      // Add token to request if available
      if (token) {
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        config.headers.Authorization = authToken;
      }
    }
    
    // Ensure we're not sending undefined values in the request
    if (config.params) {
      Object.keys(config.params).forEach(key => {
        if (config.params[key] === undefined || config.params[key] === '') {
          delete config.params[key];
        }
      });
    }
    
    logger.debug(`[API] Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`, {
      params: config.params,
      headers: config.headers,
      data: config.data
    });
    
    return config;
  },
  (error) => {
    console.error('[API] Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors and log responses
api.interceptors.response.use(
  (response) => {
    // Track API performance
    if (response.config._startTime) {
      const duration = performance.now() - response.config._startTime;
      performanceService.trackApiCall({
        url: response.config.url,
        method: response.config.method?.toUpperCase() || 'GET',
        status: response.status,
        duration,
        success: true,
        timestamp: Date.now()
      });
    }
    
    logger.debug(`[API] Response: ${response.config.method.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      data: response.data
    });
    return response;
  },
  async (error) => {
    // Track API performance for failed requests
    if (error.config?._startTime) {
      const duration = performance.now() - error.config._startTime;
      performanceService.trackApiCall({
        url: error.config.url,
        method: error.config.method?.toUpperCase() || 'GET',
        status: error.response?.status || 0,
        duration,
        success: false,
        error: error.message,
        timestamp: Date.now()
      });
    }
    
    const originalRequest = error.config;
    
    // If error is 401 (authentication) and we haven't tried to refresh yet
    // Note: 403 (permission) errors should NOT trigger token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      originalRequest._retry = true;
      
      try {
        // Try to refresh the session
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) throw refreshError;
        
        if (data?.session?.access_token) {
          // Save the new token
          tokenManager.setToken(data.session.access_token);
          
          // Update the Authorization header
          originalRequest.headers.Authorization = `Bearer ${data.session.access_token}`;
          
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Only log unexpected refresh errors (not auth errors or session issues)
        if (refreshError.status !== 500 && !refreshError.message?.includes('oauth_client_id')) {
          console.error('Failed to refresh session:', refreshError);
        }
        // If refresh fails, clear the token and redirect to login
        tokenManager.removeToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // If the request was cancelled (AbortController) or aborted (timeout/unmount), ignore it silently
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED') {
      return Promise.reject(error);
    }
    
    // If the error is due to network issues
    if (!error.response) {
      console.error('Network error - please check your internet connection');
      return Promise.reject({ ...error, message: 'Network error - please check your internet connection' });
    }

    const { status, data } = error.response;
    
    // Handle specific status codes
    switch (status) {
      case 401: // Unauthorized
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          console.error('Authentication error: Unauthorized access. Redirecting to login.');
          tokenManager.removeToken();
          // Use replace instead of href to prevent adding to history
          window.location.replace('/login');
        }
        break;
        
      case 403: // Forbidden
        // Check if this is a usage limit error
        if (error.response?.data?.detail?.error === 'usage_limit_exceeded') {
          console.error('Usage limit exceeded:', error.response.data.detail);
        } else {
          console.error('Forbidden - you do not have permission.');
        }
        // Don't redirect - 403 is a permission error, not an authentication error
        break;
        
      case 404: // Not Found
        console.error('Resource not found');
        break;
        
      case 429: // Too Many Requests
        console.error('Too many requests - please slow down');
        // Optionally implement retry logic
        break;
        
      case 500: // Internal Server Error
      case 502: // Bad Gateway
      case 503: // Service Unavailable
      case 504: // Gateway Timeout
        console.error(`Server error (${status}) - please try again later`);
        
        // Implement retry logic for server errors
        if (!originalRequest._retry && status !== 500) {
          originalRequest._retry = true;
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(api(originalRequest));
            }, 2000); // Wait 2 seconds before retrying
          });
        }
        break;
        
      default:
        console.error(`Error (${status}):`, data?.detail || 'An error occurred');
    }
    
    // Return a consistent error format
    return Promise.reject({
      status: error.response?.status,
      message: data?.detail || error.message || 'An error occurred',
      data: data,
    });
  }
);

// API methods
export const auth = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
};

export const projects = {
  getAll: (params = {}) => api.get('/projects/', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (projectData) => api.post('/projects/', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  delete: (id) => api.delete(`/projects/${id}`),
  search: (query) => api.get('/projects/search', { params: { q: query } }),
  getRecent: (limit = 10) => api.get('/projects/recent', { params: { limit } }),
};

export const permissions = {
  getProjectAccess: (projectId) => api.get(`/permissions/projects/${projectId}/access`),
  checkProjectPermission: (projectId, requiredRole) => api.get(`/permissions/projects/${projectId}/check-permission`, { 
    params: { required_role: requiredRole } 
  }),
  getMyProjects: (options = {}) => api.get('/permissions/my-projects', { params: options }),
  getProjectMembersWithRoles: (projectId) => api.get(`/permissions/projects/${projectId}/members-with-roles`),
};

export const workItems = {
  getAll: (projectId, options = {}) => api.get(`/projects/${projectId}/work-items`, { params: options }),
  getById: (workItemId) => api.get(`/work-items/${workItemId}`),
  create: (projectId, workItemData) => api.post(`/projects/${projectId}/work-items`, workItemData),
  update: (workItemId, updateData) => api.patch(`/work-items/${workItemId}`, updateData),
  delete: (workItemId) => api.delete(`/work-items/${workItemId}`),
  comments: {
    getAll: (workItemId, options = {}) => api.get(`/work-items/${workItemId}/comments`, { params: options }),
    create: (workItemId, commentData) => api.post(`/work-items/${workItemId}/comments`, commentData),
    update: (commentId, updateData) => api.put(`/work-items/comments/${commentId}`, updateData),
    delete: (commentId) => api.delete(`/work-items/comments/${commentId}`),
  }
};

export const features = {
  getAll: (projectId, options = {}) => api.get(`/projects/${projectId}/features`, { params: options }),
  getById: (featureId) => api.get(`/features/${featureId}`),
  create: (projectId, featureData) => api.post(`/projects/${projectId}/features`, featureData),
  update: (featureId, updateData) => api.put(`/features/${featureId}`, updateData),
  delete: (featureId) => api.delete(`/features/${featureId}`),
  attachments: {
    getAll: (featureId) => api.get(`/features/${featureId}/attachments`),
    getPublic: (featureId) => api.get(`/features/${featureId}/attachments/public`),
    presign: (featureId, fileData) => api.post(`/features/${featureId}/attachments/presign`, fileData),
    finalize: (featureId, attachmentData) => api.post(`/features/${featureId}/attachments/finalize`, attachmentData),
    update: (featureId, attachmentId, updateData) => api.patch(`/features/${featureId}/attachments/${attachmentId}`, updateData),
    delete: (featureId, attachmentId) => api.delete(`/features/${featureId}/attachments/${attachmentId}`),
  }
};

export const promotions = {
  // Public endpoints
  getActive: (options = {}) => api.get('/promotions/active', { params: options }),
  validateCoupon: (code, planName) => api.post('/coupons/validate', { code, plan_name: planName }),
  
  // Admin endpoints (superuser only)
  admin: {
    createPromotion: (promotionData) => api.post('/admin/promotions', promotionData),
    listPromotions: (options = {}) => api.get('/admin/promotions', { params: options }),
    getPromotion: (promotionId) => api.get(`/admin/promotions/${promotionId}`),
    updatePromotion: (promotionId, updateData) => api.patch(`/admin/promotions/${promotionId}`, updateData),
    deletePromotion: (promotionId) => api.delete(`/admin/promotions/${promotionId}`),
    
    createCoupon: (couponData) => api.post('/admin/coupons', couponData),
    listCoupons: (options = {}) => api.get('/admin/coupons', { params: options }),
    getCoupon: (couponId) => api.get(`/admin/coupons/${couponId}`),
    updateCoupon: (couponId, updateData) => api.patch(`/admin/coupons/${couponId}`, updateData),
    deleteCoupon: (couponId) => api.delete(`/admin/coupons/${couponId}`),
    getCouponStats: (couponId) => api.get(`/admin/coupons/${couponId}/stats`),
  }
};

export default api;

